// 产品数据加载骨架（HomePage / TagPage / SearchPage 共用）
//
// 此前三页各自复制了几乎相同的「加载 index.json → 拼 dateRanges → 批量回填 IndexedDB
// 缓存 → 补齐缺失区间（非最新日期才缓存）→ 写回」流程，仅在筛选/搜索/路由回写上不同。
// 这里把可共用的「加载」部分抽成纯函数，页面侧只保留自己的数据消费与路由编排。
//
// 设计取舍：导出的是普通异步函数而非 Vue composable，便于 Options API 页面直接在
// methods 里调用，无需引入 setup()；所有缓存策略（非最新才缓存、按 start 排序取最新）
// 统一收敛到 cacheManager，避免再次出现 SearchPage 那样的本地分叉实现。

import { getData, storeData, getAllData, isLatestDateRange, cleanExpiredData } from './cacheManager';
import { resolveDataUrl } from './dataResolver';
import { ALL_TIME_RANGE } from './dateHierarchy';

export const INDEX_URL = 'data/index.json';

// 加载 index.json，返回按 start 日期升序的 indexData 与拼好的 dateRanges（"YYYY-MM-DD - YYYY-MM-DD"）
export async function fetchIndex() {
  const res = await fetch(resolveDataUrl(INDEX_URL));
  if (!res.ok) throw new Error('Failed to load index.json');
  const indexData = await res.json();
  const sorted = [...indexData.files].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const dateRanges = sorted.map((f) => `${f.startDate} - ${f.endDate}`);
  // 缓存清理单点收敛：任何经此入口的页面（首页/标签页/搜索页）都顺带清理 7 天过期缓存，
  // 避免用户从 /tag 或 /search 直达时 IndexedDB 只增不减（此前只有首页会调 cleanExpiredData）。
  await cleanExpiredData();
  return { indexData: { ...indexData, files: sorted }, dateRanges };
}

// 三页共用的「URL 日期优先、否则按策略取默认周」解析，消除各自重复的默认分支漂移风险。
// defaultPolicy:
//   'latest'  —— 首页：默认最新周（files 升序后的最后一项）
//   'allTime' —— 标签页/搜索页：默认「全部时间」哨兵
// 仅负责计算 selectedDateRange；URL 回写（路由格式各页不同）仍由各页自行处理。
export function resolveInitialRange({ urlDate, dateRanges, indexData, defaultPolicy = 'latest' }) {
  if (urlDate && dateRanges.includes(urlDate)) {
    return urlDate;
  }
  if (defaultPolicy === 'latest') {
    const latestEntry = indexData?.files?.[indexData.files.length - 1];
    return latestEntry ? `${latestEntry.startDate} - ${latestEntry.endDate}` : null;
  }
  // allTime：标签页 / 搜索页默认落到「全部时间」合并视图
  return ALL_TIME_RANGE;
}

// 由 dateRange 在 indexData.files 中定位数据文件路径
export function getFilePathForDateRange(indexData, dateRange) {
  return indexData.files.find(
    (f) => `${f.startDate} - ${f.endDate}` === dateRange
  )?.file;
}

// 单区间按需加载：内存已存在则直接返回；否则 IndexedDB(getData) → 服务器(fetch)，
// 非最新日期才 storeData。返回该区间数据（写入 dataMap）。
export async function loadSingleRange({ dataMap, range, indexData, dateRanges }) {
  if (dataMap[range]) return dataMap[range];

  if (!isLatestDateRange(range, dateRanges)) {
    const cached = await getData(range, dateRanges);
    if (cached) {
      dataMap[range] = cached;
      return cached;
    }
  }

  const file = getFilePathForDateRange(indexData, range);
  if (!file) throw new Error('No matching data file found');
  const res = await fetch(resolveDataUrl(file));
  if (!res.ok) throw new Error('Failed to load data from server');
  const data = await res.json();

  if (!isLatestDateRange(range, dateRanges)) {
    await storeData(data, range);
  }
  dataMap[range] = data;
  return data;
}

// 单区间补齐：IndexedDB(仅非最新) → fetch → 写回 dataMap（仅非最新才 storeData）。
// 与 loadSingleRange 的差别：这里全部静默处理，失败只记日志、不向外抛。
async function fillOneRange({ dataMap, range, indexData, dateRanges }) {
  // 最新周的数据会变，因此既不读缓存也不写缓存；非最新周才有缓存语义
  const cacheable = !isLatestDateRange(range, dateRanges);

  if (cacheable) {
    const cached = await getData(range, dateRanges);
    if (cached) {
      dataMap[range] = cached;
      return;
    }
  }

  const file = getFilePathForDateRange(indexData, range);
  if (!file) return;

  const res = await fetch(resolveDataUrl(file));
  if (!res.ok) return;
  const data = await res.json();

  dataMap[range] = data;
  if (cacheable) {
    await storeData(data, range);
  }
}

// 预取并发上限。历史上这里是 for...of + await 的串行实现：114 周 / 44MB 实测平均
// 1.43s/个，合计约 2.5 分钟才能补齐（标签页/搜索页必须补齐所有周才算得出「全部时间」
// 命中，用户要对着骨架等全程）。改成有上限的并发后同一批数据降到数十秒量级。
// 上限刻意保守：再高收益递减，且会挤占同页其他请求（图片、index.json）。
export const FILL_CONCURRENCY = 6;

// 批量把缺失区间填入 dataMap（内存已存在的跳过）。
// 非最新日期优先走 IndexedDB(getData)，否则 fetch 并 storeData（仅非最新缓存）。
// 行为等价于旧 HomePage.preloadOtherDatesData / TagPage.loadAllData 内循环 / SearchPage.loadMissingData，
// 差别仅在于并发拉取——写回顺序不再与 dateRanges 顺序一致（dataMap 是按键索引的，
// 调用方不依赖顺序；唯一要小心的 `rangeCounts` 等派生值都在 await 之后才计算）。
export async function fillMissingRanges({
  dataMap,
  indexData,
  dateRanges,
  concurrency = FILL_CONCURRENCY,
}) {
  const missing = dateRanges.filter((r) => !dataMap[r]);
  if (!missing.length) return;

  // worker pool：cursor 在 JS 单线程里自增取号，天然无竞态
  let cursor = 0;
  const worker = async () => {
    while (cursor < missing.length) {
      const range = missing[cursor++];
      try {
        await fillOneRange({ dataMap, range, indexData, dateRanges });
      } catch (e) {
        console.error(`[productData] load failed for ${range}:`, e);
      }
    }
  };

  const slots = Math.max(1, Math.min(concurrency, missing.length));
  await Promise.all(Array.from({ length: slots }, () => worker()));
}

// 重新导出，避免页面侧再从 cacheManager 单独引入
export { getAllData, isLatestDateRange };
